import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { webddn } from 'src/environments/webddn';
import { ProfileService } from './profile.service';
import { BookAppointmentService } from '../book-appointment/book-appointment.service';

/**
 * Token-only session: resolves passenger via ProfileService (Bearer token), then cases API.
 * "Pending" matches header nav: show Patient Intake when no active case has completed intake.
 */
@Injectable({ providedIn: 'root' })
export class PatientIntakeGateService {
  private intakeRefresh$ = new Subject<void>();
  readonly intakeRefreshed$ = this.intakeRefresh$.asObservable();

  constructor(
    private profileService: ProfileService,
    private bookAppointmentService: BookAppointmentService
  ) {}

  notifyIntakeCompleted(): void {
    this.intakeRefresh$.next();
  }

  patientIntakePending$(role: string | undefined): Observable<boolean> {
    if (webddn.APP_TYPE !== 'health' && webddn.APP_TYPE !== 'optical' || role !== 'u') {
      return of(false);
    }
    return this.profileService.getProfile().pipe(
      switchMap((res: any) => {
        const profile = res?.data?.profile;
        if (!profile) {
          return of(true);
        }
        const passengerId = profile.passenger_id ?? profile.passengerId ?? profile._id;
        const email = profile.email ?? profile.username;
        if (passengerId) {
          return this.bookAppointmentService.getPatientCases(String(passengerId)).pipe(
            map((r) => this.computePending(r)),
            catchError(() => of(true))
          );
        }
        if (email) {
          return this.bookAppointmentService.getPatientCasesByEmail(String(email)).pipe(
            map((r) => this.computePending(r)),
            catchError(() => of(true))
          );
        }
        return of(true);
      }),
      catchError(() => of(true))
    );
  }

  private computePending(res: any): boolean {
    const activeCases = (res?.cases || []).filter((c: any) => c.status === 'active');
    return !activeCases.some((c: any) => c.caseType && c.hasCompletedIntake);
  }
}
